
import React, { useState, useEffect } from 'react'
import axios from 'axios';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { baseUrl, limit, formatDate, formatMasterReportValue } from "../../comman/config";
import * as XLSX from 'xlsx';

import Loader from '../../components/Loader';
import { ItemName, itemDisplayName, itemExportName } from "../../components/ItemName";

// Excess Report (mobile layout). Two rows per menu item -- Actual and vs. Planned -- across the
// Excess / Shortage figures, then the item's FG qty, FG / Person and Remarks alongside.
//
// Every value is quoted from the Raw Report payload (/reports/master_report), so the two screens
// cannot drift. Actual is the Raw Report's Actual (physical) column and vs. Planned its net
// counterpart -- the same pairing the Raw Report shows under Excess / Shortage Qty. and
// Excess / Shortage Bowls.
const EXCESS_ROWS = [
    {
        key: 'actual', label: 'Actual',
        kgs: { v: (r) => r.excess_fg_qty, f: 'excess_fg_qty' },
        bowls: { v: (r) => r.excess_bowls_physical, f: 'excess_bowls_physical' },
        persons: { v: (r) => r.excess_fg_persons, f: 'excess_fg_persons' },
    },
    {
        key: 'planned', label: 'vs. Planned',
        kgs: { v: (r) => r.net_excess_fg_kgs, f: 'net_excess_fg_kgs' },
        bowls: { v: (r) => r.excess_bowls_net, f: 'excess_bowls_net' },
        persons: { v: (r) => r.net_excess_fg_persons, f: 'net_excess_fg_persons' },
    },
];

// Column widths, one entry per column in table order, applied through a <colgroup> on a
// table-layout: fixed table. Fixed layout is the point of it: max-width on a table cell is
// undefined in the automatic algorithm and browsers largely ignore it, so the caps this table
// used to carry never bound anything and the sixth column kept being pushed off a phone.
//
// The first six -- Menu Item, the Actual / vs. Planned label, Kgs., Bowls, Persons and
// % Diff (vs Plan) -- add up to 352px, which lands inside a 360px phone now that the wrapper
// drops its side padding there. The seven beyond them are what the sideways scroll is for.
// Everything widens from sm up, where the type goes from 11px to 14px.
const COL_WIDTHS = [
    'w-[72px] sm:w-[128px]',  // Menu Item (sticky)
    'w-[64px] sm:w-[96px]',   // Actual / vs. Planned -- sized so neither label wraps
    'w-[54px] sm:w-[80px]',   // Excess / Shortage Kgs.
    'w-[54px] sm:w-[80px]',   // Excess / Shortage Bowls
    'w-[54px] sm:w-[80px]',   // Excess / Shortage Persons
    'w-[54px] sm:w-[80px]',   // % Diff (vs Plan)
    'w-[58px] sm:w-[88px]',   // FG qty Planned
    'w-[58px] sm:w-[88px]',   // FG qty Production
    'w-[58px] sm:w-[88px]',   // FG qty % Diff
    'w-[58px] sm:w-[88px]',   // FG / Person Planned
    'w-[58px] sm:w-[88px]',   // FG / Person Actual
    'w-[58px] sm:w-[88px]',   // FG / Person % Diff
    'w-[104px] sm:w-[176px]', // Remarks
];

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Formatted the way the Raw Report formats that same field, so a cell reads identically on both.
const show = (cell, row) => {
    if (!cell) return '';
    const value = cell.v(row);
    if (value === null || value === undefined || value === '') return '-';
    return formatMasterReportValue(value, cell.f);
};

const pct = (numerator, denominator) => {
    const d = num(denominator);
    if (d === 0) return null;
    return (num(numerator) / d) * 100;
};

const pctText = (value) => (value === null ? '-' : `${value.toFixed(2)}%`);

// % Diff = (b - a) / a. Nothing to compare against when the baseline is 0.
const pctDiff = (a, b) => {
    const base = num(a);
    if (base === 0) return null;
    return ((num(b) - base) / base) * 100;
};

// The report reads in kgs, so each item shows the row the Raw Report presents as Kgs: genuine Kgs
// (method 1), the ltr/nos -> Kgs mirror rows (also method 1) and Readymade-Nos (6). Exactly one row
// per item qualifies. An item with no such row falls back to its first row rather than dropping off.
const KGS_METHOD_IDS = [1, 6];

const groupByItem = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
        const key = `${row.date}|${row.session}|${row.menu_item_id}`;
        const existing = groups.get(key);
        const preferred = KGS_METHOD_IDS.includes(Number(row.methodName?.id));
        if (!existing || (preferred && !existing.preferred)) {
            groups.set(key, { key, row, preferred });
        }
    });
    return [...groups.values()].map((g) => g.row);
};

// vs. Planned rows carry a light blue tint. The Actual row keeps the default white, and the cells
// spanning both rows (FG qty, FG / Person, Remarks) stay untinted -- they belong to the item, not
// to either line.
const VS_PLANNED_BG = '#dbeafe';

// Remarks are free text; the cell shows a short prefix and the whole thing opens on click.
const REMARKS_CHAR_LIMIT = 20;
const truncate = (text) => {
    const full = String(text ?? '');
    return full.length > REMARKS_CHAR_LIMIT ? `${full.slice(0, REMARKS_CHAR_LIMIT)}...` : full;
};

// Session defaults to whichever meal the current time falls in. Session master ids: 1 Lunch,
// 2 Dinner, 3 Breakfast -- the mapping the Dish Issue API sync uses.
const sessionForNow = () => {
    const h = new Date().getHours();
    if (h < 11) return 3;
    if (h < 16) return 1;
    return 2;
};

function List() {

    const today = new Date().toISOString().split("T")[0];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(today);
    const [session, setSession] = useState(null);
    const [sessionList, setSessionList] = useState([]);
    // Remarks popup: the row whose full text is open.
    const [remarksRow, setRemarksRow] = useState(null);

    useEffect(() => {
        getSessionList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSessionList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
        const list = (response.data.data ?? []).map((s) => ({ value: s.id, label: s.name }));
        setSessionList(list);
        // Default the session by time of day, then run the report for it.
        const preset = list.find((s) => Number(s.value) === sessionForNow()) ?? null;
        setSession(preset);
        getReport(today, preset);
    }

    // Args are passed explicitly by Submit -- state set in the same tick is still stale here.
    const getReport = async (d = date, s = session) => {
        setLoading(true);
        try {
            const sessionParam = s ? [s] : [];
            const response = await axios.get(`${baseUrl}/reports/master_report?page=1&limit=${limit}&fromDate=${d}&toDate=${d}` +
                `&session=${encodeURIComponent(JSON.stringify(sessionParam))}&item=${encodeURIComponent(JSON.stringify([]))}&method=${encodeURIComponent(JSON.stringify([]))}&itemFilter=in`);
            setRows(response.data.data ?? []);
        } catch (e) {
            console.error('Failed to load excess report', e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }

    const handleClear = () => {
        const preset = sessionList.find((s) => Number(s.value) === sessionForNow()) ?? null;
        setDate(today);
        setSession(preset);
        getReport(today, preset);
    }

    const items = groupByItem(rows.filter((r) => r.measurements));

    // Count (persons) is a per date+session figure, not a per item one, so every row of a session
    // repeats it -- hence one entry per distinct session rather than per row.
    const countSlots = [...items.reduce((acc, r) => {
        const key = `${r.date}|${r.session}`;
        if (!acc.has(key)) {
            acc.set(key, {
                planned: num(r.raw_material?.estimated_count),
                coupon: num(r.pass_issued),
                dish: num(r.final_dish_count),
            });
        }
        return acc;
    }, new Map()).values()];
    const counts = countSlots.reduce((a, s) => ({
        planned: a.planned + s.planned,
        coupon: a.coupon + s.coupon,
        dish: a.dish + s.dish,
    }), { planned: 0, coupon: 0, dish: 0 });
    // % Diff (Planned) = (Dish Issued - Planned) / Planned.
    const countDiff = pctDiff(counts.planned, counts.dish);

    const multiSession = new Set(items.map((r) => r.session)).size > 1;

    const handleExport = () => {
        const header = [
            "Date", "Session", "Menu Item", "",
            "Excess / Shortage Kgs.", "Excess / Shortage Bowls", "Excess / Shortage Persons", "% Diff (vs Plan)",
            "FG qty Planned", "FG qty Production", "FG qty % Diff",
            "FG / Person (gms.) Planned", "FG / Person (gms.) Actual", "FG / Person (gms.) % Diff",
            "Remarks",
        ];
        const data = [];
        items.forEach((row) => {
            EXCESS_ROWS.forEach((er, ri) => {
                const first = ri === 0;
                data.push([
                    formatDate(row.date),
                    row.session_name,
                    itemExportName(row),
                    er.label,
                    show(er.kgs, row),
                    show(er.bowls, row),
                    show(er.persons, row),
                    pctText(pct(er.kgs.v(row), row.planned_fg)),
                    // Per item, not per row -- repeating these on the vs. Planned line would read
                    // as a second, different figure.
                    first ? formatMasterReportValue(row.planned_fg ?? 0, 'planned_fg') : '',
                    first ? formatMasterReportValue(row.finishedQty ?? 0, 'finishedQty') : '',
                    first ? pctText(pctDiff(row.planned_fg, row.finishedQty)) : '',
                    first ? formatMasterReportValue(row.planned_fg_per_person ?? 0, 'planned_fg_per_person') : '',
                    first ? formatMasterReportValue(row.fg_consumption_per_person ?? 0, 'fg_consumption_per_person') : '',
                    first ? pctText(pctDiff(row.planned_fg_per_person, row.fg_consumption_per_person)) : '',
                    first ? (row.raw_report_remarks ?? '') : '',
                ]);
            });
        });

        const worksheet = XLSX.utils.aoa_to_sheet([
            ["Count (persons)", "Planned", "Coupon Sold", "Dish Issued", "% Diff (Planned)"],
            ["", counts.planned, counts.coupon, counts.dish, pctText(countDiff)],
            [],
            header,
            ...data,
        ]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Excess Report");
        XLSX.writeFile(workbook, `Excess_report_${formatDate(date)}${session?.label ? '_' + session.label : ''}.xlsx`);
    }

    // Hairline padding and 11px type below sm, so the figures sit inside the narrow columns
    // COL_WIDTHS gives them. "% Diff (vs Plan)" wraps rather than overflowing its column.
    const cell = "px-0.5 py-1 border border-black text-center whitespace-nowrap text-[11px] sm:text-sm";
    const head = "px-0.5 py-1 border border-black text-center text-[11px] sm:text-sm";
    const stickyItem = { position: 'sticky', left: 0, zIndex: 1, background: '#f9fafb' };

    return (
        <div className="p-2 sm:p-4">
            {loading && <Loader/>}
            <div className="flex flex-wrap justify-between items-center gap-2 py-3 px-2 sm:px-6">
                <h1 className="text-xl sm:text-2xl font-bold">Excess Report</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExport}
                        className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none">
                        Export
                    </button>
                    <Link to="/">
                        <button className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none">Back</button>
                    </Link>
                </div>
            </div>

            {/* Date defaults to today, session to whichever meal the current time falls in. */}
            <div className="flex flex-wrap items-center gap-2 px-2 sm:px-6 pb-3">
                <input
                    type="date"
                    className="p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                />
                <div className="w-40">
                    <Select
                        isClearable
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        value={session}
                        onChange={(selected) => setSession(selected)}
                        options={sessionList}
                        placeholder="Session"
                    />
                </div>
                <button
                    onClick={() => getReport(date, session)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none">
                    Submit
                </button>
                <button
                    onClick={handleClear}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none">
                    Clear
                </button>
            </div>

            {/* Count (persons) summary. */}
            <div className="px-2 sm:px-6 pb-4 overflow-x-auto">
                <table className="text-xs sm:text-sm text-gray-900" style={{ borderCollapse: 'collapse' }}>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className={head}></th>
                            <th className={head}>Planned</th>
                            <th className={head}>Coupon Sold</th>
                            <th className={head}>Dish Issued</th>
                            <th className={head}>% Diff (Planned)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th scope="row" className={`${head} bg-gray-50 font-medium`}>Count (persons)</th>
                            <td className={cell}>{counts.planned}</td>
                            <td className={cell}>{counts.coupon}</td>
                            <td className={cell}>{counts.dish}</td>
                            <td className={cell}>{pctText(countDiff)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Main table. The six columns up to "% Diff (vs Plan)" are sized to fit a phone screen; the
                rest scrolls, with Menu Item pinned so a scrolled row stays identifiable. It runs edge
                to edge on a phone -- the 8px of side padding the rest of the screen carries is 16px
                the six columns would otherwise have to come out of. */}
            <div className="px-0 sm:px-6 overflow-x-auto">
                <table className="w-max text-xs sm:text-sm text-gray-900"
                    style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                    <colgroup>
                        {COL_WIDTHS.map((w, i) => <col key={i} className={w} />)}
                    </colgroup>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className={head} rowSpan={2} style={{ ...stickyItem, zIndex: 2 }}>Menu Item</th>
                            <th className={head} rowSpan={2}></th>
                            <th className={head} colSpan={4}>Excess / Shortage Qty.</th>
                            <th className={head} colSpan={3}>FG qty</th>
                            <th className={head} colSpan={3}>FG / Person (gms.)</th>
                            <th className={head} rowSpan={2}>Remarks<br/><span className="font-normal">(from Raw Report)</span></th>
                        </tr>
                        <tr>
                            <th className={head}>Kgs.</th>
                            <th className={head}>Bowls</th>
                            <th className={head}>Persons</th>
                            <th className={`${head} whitespace-normal`}>% Diff (vs Plan)</th>
                            <th className={head}>Planned</th>
                            <th className={head}>Production</th>
                            <th className={head}>% Diff</th>
                            <th className={head}>Planned</th>
                            <th className={head}>Actual</th>
                            <th className={head}>% Diff</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row) => {
                            const remarks = row.raw_report_remarks ?? '';
                            return EXCESS_ROWS.map((er, ri) => {
                                const first = ri === 0;
                                // The vs. Planned line is tinted so the two rows of an item are told
                                // apart at a glance -- the figures alone look alike. Inline rather
                                // than a class so it also wins over the label cell's own bg-gray-50.
                                const rowBg = er.key === 'planned' ? { backgroundColor: VS_PLANNED_BG } : undefined;
                                return (
                                    <tr key={`${row.date}|${row.session}|${row.menu_item_id}|${er.key}`}>
                                        {first && (
                                            <th scope="row" rowSpan={2}
                                                className={`${head} font-medium text-left`}
                                                style={stickyItem}>
                                                <ItemName item={row} />
                                                {multiSession && <span className="block font-normal text-[10px]">{row.session_name}</span>}
                                            </th>
                                        )}
                                        <th scope="row" className={`${head} bg-gray-50 font-normal text-left`} style={rowBg}>{er.label}</th>
                                        <td className={cell} style={rowBg}>{show(er.kgs, row)}</td>
                                        <td className={cell} style={rowBg}>{show(er.bowls, row)}</td>
                                        <td className={cell} style={rowBg}>{show(er.persons, row)}</td>
                                        {/* Excess / Shortage Kgs. as a share of the PLANNED FG qty, on
                                            both lines: Actual against plan, and vs. Planned against plan. */}
                                        <td className={cell} style={rowBg}>{pctText(pct(er.kgs.v(row), row.planned_fg))}</td>
                                        {first && (
                                            <>
                                                <td className={cell} rowSpan={2}>{formatMasterReportValue(row.planned_fg ?? 0, 'planned_fg')}</td>
                                                <td className={cell} rowSpan={2}>{formatMasterReportValue(row.finishedQty ?? 0, 'finishedQty')}</td>
                                                <td className={cell} rowSpan={2}>{pctText(pctDiff(row.planned_fg, row.finishedQty))}</td>
                                                <td className={cell} rowSpan={2}>{formatMasterReportValue(row.planned_fg_per_person ?? 0, 'planned_fg_per_person')}</td>
                                                <td className={cell} rowSpan={2}>{formatMasterReportValue(row.fg_consumption_per_person ?? 0, 'fg_consumption_per_person')}</td>
                                                <td className={cell} rowSpan={2}>{pctText(pctDiff(row.planned_fg_per_person, row.fg_consumption_per_person))}</td>
                                                <td className={`${head} text-left`} rowSpan={2} style={{ minWidth: 100 }}>
                                                    {remarks && (
                                                        <span
                                                            onClick={() => setRemarksRow(row)}
                                                            title={remarks}
                                                            className="cursor-pointer hover:underline">
                                                            {truncate(remarks)}
                                                        </span>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            });
                        })}
                        {!loading && items.length === 0 && (
                            <tr><td className="px-2 py-4 text-center text-gray-500" colSpan={13}>
                                No menu items for this date and session.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Full remarks, opened by clicking the truncated text. Read-only -- remarks are written
                on the Raw Report. */}
            {remarksRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setRemarksRow(null)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b">
                            <div className="font-bold text-gray-900">Remarks</div>
                            <div className="text-xs text-gray-500">
                                {itemDisplayName(remarksRow)} — {formatDate(remarksRow.date)}
                            </div>
                        </div>
                        <div className="p-4 text-sm text-gray-900 whitespace-pre-wrap break-words">
                            {remarksRow.raw_report_remarks}
                        </div>
                        <div className="flex justify-end px-4 py-3 border-t">
                            <button type="button" onClick={() => setRemarksRow(null)}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
  );
}

export default List;
